import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SESSION_EXPIRED_KEY } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import api from '../services/api';

// The lockout deadline is persisted so a page refresh keeps counting down
// against the same server-side deadline instead of appearing to restart.
const LOCKOUT_UNTIL_KEY = 'login_lockout_until';

const readStoredLockoutUntil = () => {
  try {
    const raw = localStorage.getItem(LOCKOUT_UNTIL_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || ts <= Date.now()) {
      localStorage.removeItem(LOCKOUT_UNTIL_KEY);
      return null;
    }
    return ts;
  } catch {
    return null;
  }
};

/** Post-login home aligned with ProtectedRoute role routing. */
const homePathForRole = (role) => {
  if (role === 'superadmin') return '/admin/companies';
  if (role === 'companyadmin') return '/admin/templates';
  return '/portal/templates';
};

const roleWelcomeLabel = (role) => {
  if (role === 'superadmin') return 'an administrator';
  if (role === 'companyadmin') return 'a company administrator';
  return 'a staff user';
};

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [error, setError] = useState('');
  // Which address to sign in on. A company's own page and the platform's page
  // reject each other's accounts with the same "invalid" message, so without
  // the server's hint the user cannot tell that they are simply on the wrong
  // address. Read from the 401 body, never guessed on the client.
  const [hint, setHint] = useState('');
  const { branding } = useBranding();
  const productName = branding.productName || 'inkedform';
  const loginLogoUrl = branding.hasCustomLogo && branding.logoUrl ? branding.logoUrl : '/images/lockup.png';
  const loginLogoAlt = branding.hasCustomLogo ? (branding.companyName || productName) : productName;
  const supportHref = branding.contactUrl
    || (branding.contactEmail ? `mailto:${branding.contactEmail}` : 'mailto:support@inkedform.com');
  const supportLabel = branding.contactEmail || 'support@inkedform.com';
  const [lockoutError, setLockoutError] = useState(() => (readStoredLockoutUntil() ? 'Too many failed login attempts.' : ''));
  const [loading, setLoading] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(readStoredLockoutUntil);
  const [nowMs, setNowMs] = useState(Date.now());
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const { login, verifyTwoFactor, isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where to return after login (set by guarded pages like /onboarding)
  const returnTo = location.state?.from
    ? `${location.state.from.pathname || ''}${location.state.from.search || ''}`
    : null;

  // Session-expired popup: shown when AuthContext detected an expired session
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1') {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      setShowSessionExpired(true);
    }
  }, []);
  const currentYear = new Date().getFullYear();
  const lockoutIntervalRef = useRef(null);
  const lockoutSeconds = useMemo(() => {
    if (!lockoutUntil) {
      return 0;
    }

    return Math.max(0, Math.ceil((lockoutUntil - nowMs) / 1000));
  }, [lockoutUntil, nowMs]);

  const formatLockoutDisplay = (seconds) => {
    if (seconds <= 0) {
      return '0s';
    }

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins <= 0) {
      return `${secs}s`;
    }

    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  useEffect(() => {
    if (!lockoutUntil) {
      if (lockoutIntervalRef.current) {
        window.clearInterval(lockoutIntervalRef.current);
        lockoutIntervalRef.current = null;
      }
      return undefined;
    }

    if (lockoutIntervalRef.current) {
      window.clearInterval(lockoutIntervalRef.current);
    }

    lockoutIntervalRef.current = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      if (lockoutIntervalRef.current) {
        window.clearInterval(lockoutIntervalRef.current);
        lockoutIntervalRef.current = null;
      }
    };
  }, [lockoutUntil]);

  useEffect(() => {
    if (lockoutUntil && lockoutSeconds <= 0) {
      try { localStorage.removeItem(LOCKOUT_UNTIL_KEY); } catch { /* storage unavailable */ }
      setLockoutUntil(null);
      setError('');
      setLockoutError('');
    }
  }, [lockoutUntil, lockoutSeconds]);

  // Confirm the lockout deadline with the server, which tracks it per IP and so
  // stays correct after a refresh or in a new tab. Only runs when a stored
  // deadline exists, to avoid spending a rate-limit slot on every page load.
  useEffect(() => {
    if (!readStoredLockoutUntil()) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/lockout-status');
        if (cancelled) return;
        if (data?.lockedOut && data.retryAfterSeconds > 0) {
          const until = Date.now() + data.retryAfterSeconds * 1000;
          try { localStorage.setItem(LOCKOUT_UNTIL_KEY, String(until)); } catch { /* storage unavailable */ }
          setLockoutUntil(until);
          setNowMs(Date.now());
          setLockoutError('Too many failed login attempts.');
        } else {
          // Server says the lockout is over — drop the stale local deadline.
          try { localStorage.removeItem(LOCKOUT_UNTIL_KEY); } catch { /* storage unavailable */ }
          setLockoutUntil(null);
          setLockoutError('');
        }
      } catch {
        // Endpoint unavailable — keep counting down against the stored deadline.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const renderTrustFooter = () => (
    <div style={styles.footer}>
      <div>© {currentYear} {productName}. All rights reserved.</div>
      <div style={styles.footerLinks}>
        <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
        <span style={styles.footerSep} aria-hidden="true">·</span>
        <Link to="/terms" style={styles.footerLink}>Terms</Link>
      </div>
    </div>
  );

  const EyeIcon = ({ off = false }) => (
    off ? (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.08 1.5l20.42 20.42" /><path d="M1 12s4-8 11-8 11 8 11 8" /><path d="M12 5c.44 0 .87.08 1.28.22M15.55 8.13a5 5 0 0 1 5.4 5.4l.7 1.24" /><path d="M8.61 10.36A5 5 0 0 0 6.45 14.5l-.7 1.24" /><path d="M5.07 16.32a9 9 0 0 1-1.07-4.32c0-7 7-11 11-11a9.75 9.75 0 0 1 5.43 1.54" /><path d="M12 17a5 5 0 0 0 4.19-2.29L20.49 20" /><path d="M17 12a5 5 0 0 0-5-5c-1.39 0-2.6.45-3.64 1.22" />
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
      </svg>
    )
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setHint('');
    setLockoutError('');
    setLoading(true);

    try {
      const user = await login(username, password);
      try { localStorage.removeItem(LOCKOUT_UNTIL_KEY); } catch { /* storage unavailable */ }
      setLockoutUntil(null);
      setLockoutError('');
      setRemainingAttempts(null);

      // Only superadmin requires 2FA setup
      if (user?.role === 'superadmin') {
        if (user?.requiresTwoFactorSetup) {
          setLoading(false);
          navigate('/setup-2fa', { replace: true });
          return;
        }

        if (user?.requiresTwoFactor) {
          setLoading(false);
          setChallengeToken(user.challengeToken || '');
          return;
        }
      }

      setLoading(false);
      navigate(returnTo || homePathForRole(user?.role), { replace: true });
    } catch (err) {
      const retryAfterSeconds = err.response?.data?.retryAfterSeconds;
      const nextRemainingAttempts = err.response?.data?.remainingAttempts;
      if (err.response?.status === 429 && retryAfterSeconds) {
        const until = Date.now() + (retryAfterSeconds * 1000);
        try { localStorage.setItem(LOCKOUT_UNTIL_KEY, String(until)); } catch { /* storage unavailable */ }
        setLockoutUntil(until);
        setNowMs(Date.now());
        setRemainingAttempts(0);
        setError('');
        setLockoutError('Too many failed login attempts.');
      } else {
        setLockoutError('');
        setRemainingAttempts(typeof nextRemainingAttempts === 'number' ? nextRemainingAttempts : null);
        setError(err.response?.data?.error || err.message || 'Login failed. Please check your credentials.');
        setHint(err.response?.data?.hint || '');
      }
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await verifyTwoFactor(challengeToken, twoFactorCode);

      navigate(returnTo || homePathForRole(user?.role), { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  // Paint the branded login card immediately. Do not gate on authLoading /
  // GET /auth/me — anonymous visitors always get 401 there, and waiting for it
  // only produced a blank "Loading..." state. If the session probe later finds
  // a user, the welcome branch below takes over.
  if (isAuthenticated) {
    return (
      <div style={styles.page}>
        <div style={styles.bgDecor} />
        <div style={styles.card}>
          <div style={{ ...styles.logoSection, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img
            src={loginLogoUrl}
            alt={loginLogoAlt}
            style={{ width: 240, maxWidth: '100%', maxHeight: 80, height: 'auto', objectFit: 'contain' }}
          />
            <p style={{ ...styles.subtitle, marginTop: 14 }}>Welcome{user?.username ? `, ${user.username}` : ''}!</p>
          </div>

          <div style={styles.divider} />

          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
            You are logged in as {roleWelcomeLabel(user?.role)}.
          </p>

          {user?.role === 'superadmin' || user?.role === 'companyadmin' ? (
            <button
              style={{ ...styles.submitBtn, marginBottom: 10 }}
              onClick={() => { navigate(homePathForRole(user?.role), { replace: true }); }}
            >
              Go to Admin Portal
            </button>
          ) : (
            <button
              style={{ ...styles.submitBtn, marginBottom: 10 }}
              onClick={() => { navigate(homePathForRole(user?.role), { replace: true }); }}
            >
              Go to Forms
            </button>
          )}

          <button style={styles.secondaryBtn} onClick={() => { logout(); }}>
            Logout
          </button>

          {renderTrustFooter()}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Session expired popup */}
      {showSessionExpired && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#f0a020" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px' }}>
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', color: '#2c3e50' }}>Session Expired</h3>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '1.25rem' }}>
              Your session has expired due to inactivity. Please log in again.
            </p>
            <button
              onClick={() => setShowSessionExpired(false)}
              style={{ padding: '0.7rem 2rem', borderRadius: 6, border: 'none', backgroundColor: 'var(--company-primary-color, #16c1d5)', color: 'var(--company-primary-contrast, #fff)', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {/* Decorative background */}
      <div style={styles.bgDecor} />

      <div style={styles.card}>
        {/* Logo & Brand */}
        <div style={{ ...styles.logoSection, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img
            src={loginLogoUrl}
            alt={loginLogoAlt}
            style={{ width: 240, maxWidth: '100%', maxHeight: 80, height: 'auto', objectFit: 'contain' }}
          />
          <p style={{ ...styles.subtitle, marginTop: 14 }}>
            {challengeToken
              ? 'Enter your verification code to continue'
              : branding.isCompanyBranded
                ? `Sign in to ${branding.companyName}`
                : 'Sign in to access your account'}
          </p>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Form */}
        <form onSubmit={challengeToken ? handleVerifyTwoFactor : handleSubmit}>
          {(error || lockoutError) && (
            <div style={styles.errorBox}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <span>
                {lockoutSeconds > 0 && lockoutError
                  ? `${lockoutError} Please try again in ${formatLockoutDisplay(lockoutSeconds)}.`
                  : error}
                {hint && !lockoutError && (
                  <span style={{ display: 'block', marginTop: 6, color: '#6b7280', fontSize: 12.5, lineHeight: 1.5 }}>
                    {hint}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* The lockout countdown is already shown in the red error box above,
              so no separate lockout panel is rendered here. */}

          {!challengeToken && lockoutSeconds === 0 && typeof remainingAttempts === 'number' && remainingAttempts >= 0 && (
            <div style={{ ...styles.loginHelperPanel, color: remainingAttempts <= 1 ? '#b42318' : '#7a4b00' }}>
              Remaining password attempts: {remainingAttempts} / 5
            </div>
          )}

          {!challengeToken ? (
            <>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="username">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  disabled={loading || lockoutSeconds > 0}
                  placeholder="Enter your username"
                  style={styles.input}
                  onFocus={e => { e.target.style.borderColor = 'var(--company-primary-color, #16c1d5)'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 193, 213, 0.15)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="password">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Password
                </label>
                <div style={styles.passwordInputContainer}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading || lockoutSeconds > 0}
                    placeholder="Enter your password"
                    style={{ ...styles.input, paddingRight: 42 }}
                    onFocus={e => { e.target.style.borderColor = 'var(--company-primary-color, #16c1d5)'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 193, 213, 0.15)'; }}
                    onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    style={styles.togglePasswordButton}
                    disabled={loading}
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon off={showPassword} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={styles.loginHelperPanel}>
                Two-factor authentication is enabled. Enter the 6-digit code from your authenticator app.
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="twoFactorCode">Authentication Code</label>
                <input
                  type="text"
                  id="twoFactorCode"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  disabled={loading}
                  placeholder="Enter 6-digit code"
                  inputMode="numeric"
                  maxLength={6}
                  style={styles.input}
                  onFocus={e => { e.target.style.borderColor = 'var(--company-primary-color, #16c1d5)'; e.target.style.boxShadow = '0 0 0 3px rgba(22, 193, 213, 0.15)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <button
                type="button"
                style={{ ...styles.secondaryBtn, marginBottom: 10 }}
                disabled={loading}
                onClick={() => {
                  setChallengeToken('');
                  setTwoFactorCode('');
                  setError('');
                }}
              >
                Back to Password Login
              </button>
            </>
          )}

          {!challengeToken && (
            <div style={styles.helpRow}>
              <span style={styles.helpText}>
                {lockoutSeconds > 0
                  ? 'Locked out? Contact support for help.'
                  : 'Forgot password?'}
              </span>
              {' '}
              <a href={supportHref} style={styles.helpLink}>
                {supportLabel}
              </a>
            </div>
          )}

          <button
            type="submit"
            style={{ ...styles.submitBtn, opacity: (loading || lockoutSeconds > 0) ? 0.7 : 1, cursor: (loading || lockoutSeconds > 0) ? 'not-allowed' : 'pointer' }}
            disabled={loading || lockoutSeconds > 0}
          >
            {loading ? (challengeToken ? 'Verifying...' : 'Signing In...') : (challengeToken ? 'Verify Code' : 'Sign In')}
          </button>
        </form>

        {renderTrustFooter()}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f0f4f8 0%, #e8eef3 50%, #dce5ed 100%)',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    position: 'relative',
    overflow: 'hidden',
  },
  bgDecor: {
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(22, 193, 213, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    background: '#fff',
    padding: '40px 36px 32px',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
    width: 400,
    maxWidth: '90vw',
    position: 'relative',
    zIndex: 1,
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: 8,
  },
  logo: {
    maxHeight: 56,
    width: 'auto',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    color: 'var(--company-primary-color, #16c1d5)',
    margin: '0 0 4px',
    fontWeight: 700,
    letterSpacing: '0.3px',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: 14,
    margin: 0,
    fontWeight: 400,
  },
  divider: {
    height: 1,
    background: 'linear-gradient(to right, transparent, #e5e7eb, transparent)',
    margin: '20px 0 24px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    padding: '10px 14px',
    borderRadius: 8,
    marginBottom: 16,
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 500,
  },
  fieldGroup: {
    marginBottom: 18,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 14,
    color: '#333',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    background: '#fafbfc',
  },
  passwordInputContainer: {
    position: 'relative',
    width: '100%',
  },
  togglePasswordButton: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBtn: {
    width: '100%',
    padding: '11px 0',
    background: 'linear-gradient(135deg, var(--company-primary-color, #16c1d5) 0%, #0e7c8c 100%)',
    color: 'var(--company-primary-contrast, #fff)',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 6,
    transition: 'opacity 0.2s, transform 0.1s',
    letterSpacing: '0.3px',
    cursor: 'pointer',
  },
  secondaryBtn: {
    width: '100%',
    padding: '11px 0',
    background: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 6,
    transition: 'opacity 0.2s, transform 0.1s',
    letterSpacing: '0.3px',
    cursor: 'pointer',
  },
  footer: {
    textAlign: 'center',
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px solid #f3f4f6',
    fontSize: 11,
    color: '#9ca3af',
    lineHeight: 1.6,
  },
  footerLinks: {
    marginTop: 6,
  },
  footerLink: {
    color: '#6b7280',
    textDecoration: 'none',
  },
  footerSep: {
    margin: '0 6px',
    color: '#d1d5db',
  },
  helpRow: {
    textAlign: 'center',
    margin: '4px 0 14px',
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  helpText: {
    color: '#6b7280',
  },
  helpLink: {
    color: 'var(--company-primary-color, #16c1d5)',
    fontWeight: 600,
    textDecoration: 'none',
  },
  loginHelperPanel: {
    marginBottom: '1rem',
    textAlign: 'center',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    background: '#fefbe6',
    color: '#8a6e00',
    border: '1px solid #ffe58f',
  },
};

export default LoginPage;
